import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, EventType, Router } from '@angular/router';
import { filter, map, race, Subscription, take, timer } from 'rxjs';
import { GlobalStateProviderService } from './global-state-provider.service';
import { Service } from '../models/service';

const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{4,}$/;
// Routes whose page title is the service's public catalog name (BPA-configured,
// not user data). The service usually loads after NavigationEnd, so the
// page_view is deferred until it arrives, bounded by this wait.
const SERVICE_ROUTE_PATTERN = /^\/(services|part-b|inspector)\/:/;
const SERVICE_NAME_WAIT_MS = 3000;

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  private initialized = false;
  private pendingPageView: {
    subscription: Subscription;
    flush: () => void;
  } | null = null;

  constructor(
    private readonly router: Router,
    private readonly globalState: GlobalStateProviderService
  ) {}

  /**
   * Loads gtag.js and starts page-view tracking. No-op unless measurementId
   * is a valid GA4 id and the app is not embedded in another site
   * (index.html uses referrer=unsafe-url; GA inside a host portal would
   * enable cross-site correlation). Never throws — analytics must never
   * break the app.
   */
  init(measurementId: string, isEmbedded: boolean): void {
    try {
      if (this.initialized || isEmbedded || !this.isValidId(measurementId)) {
        return;
      }
      this.initialized = true;
      this.injectGtag(measurementId);
      this.trackPageViews();
    } catch (e) {
      console.warn('Google Analytics initialization failed:', e);
    }
  }

  private isValidId(id: string): boolean {
    return (
      !!id &&
      !id.toUpperCase().includes('PLACEHOLDER') &&
      MEASUREMENT_ID_PATTERN.test(id)
    );
  }

  private injectGtag(measurementId: string): void {
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function () {
      // GA requires the Arguments object itself on the dataLayer, not an array
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.addEventListener('error', () =>
      console.warn('Google Analytics script failed to load')
    );
    document.head.appendChild(script);
  }

  private trackPageViews(): void {
    this.router.events
      .pipe(filter((event) => event.type === EventType.NavigationEnd))
      .subscribe(() => this.handleNavigation());

    // The initial navigation may already be done before init() runs
    if (this.router.navigated) {
      this.handleNavigation();
    }
  }

  private handleNavigation(): void {
    try {
      // A navigation supersedes any page_view still waiting for its service:
      // send it now with the fallback title so every navigation yields
      // exactly one page_view.
      this.flushPendingPageView();
      const template = this.buildRouteTemplate();
      const fallbackTitle = () => document.title || template;

      if (!SERVICE_ROUTE_PATTERN.test(template)) {
        this.sendPageView(template, fallbackTitle());
        return;
      }

      const routeId = this.routeServiceId();
      const current = this.globalState.currentService;
      if (current?.name && current.service_id === routeId) {
        this.sendPageView(template, current.name);
        return;
      }

      const subscription = race(
        this.globalState.getServiceChanged().pipe(
          // Only the service this route points at may title its page_view —
          // other emissions (e.g. the dashboard's synthetic "My Account Page"
          // service) must not be attached to this navigation.
          filter(
            (service): service is Service =>
              !!service?.name && (!routeId || service.service_id === routeId)
          ),
          map((service) => service.name)
        ),
        timer(SERVICE_NAME_WAIT_MS).pipe(map(() => null))
      )
        .pipe(take(1))
        .subscribe((name) => {
          this.pendingPageView = null;
          this.sendPageView(template, name ?? fallbackTitle());
        });
      this.pendingPageView = subscription.closed
        ? null
        : {
            subscription,
            flush: () => this.sendPageView(template, fallbackTitle())
          };
    } catch (e) {
      console.warn('Google Analytics page_view failed:', e);
    }
  }

  private flushPendingPageView(): void {
    const pending = this.pendingPageView;
    this.pendingPageView = null;
    if (pending && !pending.subscription.closed) {
      pending.subscription.unsubscribe();
      pending.flush();
    }
  }

  private sendPageView(template: string, title: string): void {
    try {
      // Route template only (e.g. '/services/:id'): concrete URLs carry
      // citizen-case ids in path params and OAuth code/state in query params,
      // which must never reach Google. page_location and page_referrer are
      // overridden explicitly because gtag.js otherwise attaches the real
      // location.href and document.referrer (full concrete URLs under this
      // app's referrer=unsafe-url policy) to every hit.
      const sanitizedLocation = window.location.origin + template;
      window.gtag?.('set', {
        page_location: sanitizedLocation,
        page_referrer: window.location.origin
      });
      window.gtag?.('event', 'page_view', {
        page_path: template,
        page_location: sanitizedLocation,
        page_title: title
      });
    } catch (e) {
      console.warn('Google Analytics page_view failed:', e);
    }
  }

  private buildRouteTemplate(): string {
    const segments: string[] = [];
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    while (route) {
      const path = route.routeConfig?.path;
      if (path) {
        segments.push(path);
      }
      route = route.firstChild;
    }
    return `/${segments.join('/')}`;
  }

  private routeServiceId(): string | null {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    while (route) {
      const id = route.params?.['serviceId'] ?? route.params?.['id'];
      if (id) {
        return id;
      }
      route = route.firstChild;
    }
    return null;
  }
}
